import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setPageSettings } from "../store/actions/pageActions";
import { getChangePasswordPageSettings } from "../api/setting/pageApi";
import getPageBreadcrumb from "../services/BreadcrumbServices";
import { CHANGE_PASSWORD } from "../constants/routes";
import { useDocumentTitle } from "../hooks";
import BackendLayout from "../layouts/BackendLayout";
import {PageTitle, RedirectAlert} from "../components";
import {
  FormGroup,
  FormGroupVerticalInput,
  Label,
  TextInput,
  PasswordInput,
} from "../components";
import { getInputErrorMessage } from "../utils";
import { hasErrorInput } from "../utils";
import store from "../store";
import Button from "../components/buttons/Button";
import { changePassword } from "../api/common/changePassworApi";
import PageAlert from "../components/alerts/PageAlert";
import { setAlertSuccess, setAlertError } from "../store/actions";
import { errorStatusHandle } from "../utils/error-utils";
import {
  setButtonProcessing,
  clearButtonProcessing,
  clearPageLoading,
} from '../store/actions';
import {CHANGE_PASSWORD_PAGE} from "../constants/pageName";
import useRedirectPage from "../hooks/useRedirectPage";

const ChangePasswordPage = () => {
  useRedirectPage(CHANGE_PASSWORD_PAGE);
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [formChangePassword, setFormChangePassword] = useState({
    current_password: "",
    new_password: "",
    password_confirmation: "",
  });
  const auth = store.getState().auth;

  const setChangePasswordPageSettings = async (e) => {
    const pageSettings = await getChangePasswordPageSettings();
    dispatch(setPageSettings(pageSettings));
    dispatch(clearPageLoading());
  };

  useEffect(() => {
    setChangePasswordPageSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const halfWidthRegex = /^[\u0020-\u007E\uFF61-\uFF9F]*$/;

  const handleChangePassword = (e) => {
    if (halfWidthRegex.test(e.target.value)) {
      setFormChangePassword({
        ...formChangePassword,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleSubmitUpdate = () => {
    dispatch(setButtonProcessing("button-change-password"));
    changePassword(formChangePassword)
      .then((response) => {
        setFormChangePassword({
          current_password: "",
          new_password: "",
          password_confirmation: "",
        });
        dispatch(clearButtonProcessing("button-change-password"));
        setErrors({});
        dispatch(setAlertSuccess(response.message));
      })
      .catch((error) => {
        dispatch(clearButtonProcessing("button-change-password"));
        errorStatusHandle(error);
        if (error.status === 400) {
          let responseData = error.data;
          dispatch(setAlertError(responseData.error_message));
          setErrors(error.data.errors);
          setFormAfterHasError(error.data.errors);
        }
      });
  };

  const setFormAfterHasError = (errors) => {
    let newFormChangePassword = { ...formChangePassword };
    for (const error in errors) {
      newFormChangePassword[error] = "";
    }
    setFormChangePassword(newFormChangePassword);
  };

  const goBack = () => {
    window.history.back();
  };

  const breadcrumb = getPageBreadcrumb(CHANGE_PASSWORD);
  useDocumentTitle("パスワード変更 - 点検王");
  return (
    <BackendLayout breadcrumb={breadcrumb}>
      <PageTitle title="パスワード変更" />
      <div className="w-full h-full xl:pt-3 pt-2 2xl:w-[1000px] xl:w-[920px]">
        <RedirectAlert />
        <PageAlert />
        <div className="text-[15px] lg:text-[18px] xl:pb-3 pb-2 font-light">
          <p>現在のパスワードを変更してください。</p>
          <p>次回のサインイン時には、新しいパスワードを使用してください。</p>
        </div>
        <div className="xl:py-[10px] py-2 mt-2">
          <FormGroup direction="horizontal">
            <Label text="ユーザーID" htmlFor="username" className="w-[260px]" />
            <FormGroupVerticalInput>
              <TextInput
                className="xl:w-[440px] lg:w-[400px] w-[320px]"
                name="username"
                id="username"
                value={auth.user.username}
                readOnly
              />
            </FormGroupVerticalInput>
          </FormGroup>
          <FormGroup direction="horizontal">
            <Label
              text="現在のパスワード"
              htmlFor="current_password"
              className="w-[260px]"
              required
            />
            <FormGroupVerticalInput
              error={getInputErrorMessage(errors, "current_password")}
            >
              <PasswordInput
                className="xl:w-[440px] lg:w-[400px] w-[320px]"
                name="current_password"
                id="current_password"
                autoFocus
                maxLength={64}
                viewPassword={true}
                value={formChangePassword.current_password}
                onChange={(e) => handleChangePassword(e)}
                hasError={hasErrorInput(errors, "current_password")}
              />
            </FormGroupVerticalInput>
          </FormGroup>
          <FormGroup direction="horizontal">
            <Label
              text="新しいパスワード"
              htmlFor="new_password"
              className="w-[260px]"
              required
            />
            <FormGroupVerticalInput
              error={getInputErrorMessage(errors, "new_password")}
            >
              <PasswordInput
                className="xl:w-[440px] lg:w-[400px] w-[320px]"
                name="new_password"
                id="new_password"
                maxLength={64}
                viewPassword={true}
                value={formChangePassword.new_password}
                onChange={(e) => handleChangePassword(e)}
                hasError={hasErrorInput(errors, "new_password")}
              />
            </FormGroupVerticalInput>
          </FormGroup>
          <FormGroup direction="horizontal">
            <Label
              text="新しいパスワード（確認）"
              htmlFor="password_confirmation"
              className="w-[260px]"
              required
            />
            <FormGroupVerticalInput
              error={getInputErrorMessage(errors, "password_confirmation")}
            >
              <PasswordInput
                className="xl:w-[440px] lg:w-[400px] w-[320px]"
                name="password_confirmation"
                id="password_confirmation"
                maxLength={64}
                value={formChangePassword.password_confirmation}
                onChange={(e) => handleChangePassword(e)}
                viewPassword={true}
                hasError={hasErrorInput(errors, "password_confirmation")}
              />
            </FormGroupVerticalInput>
          </FormGroup>
          <div className="ml-[260px] mt-5">
            <div className="xl:w-[440px] lg:w-[400px] w-[320px] flex xl:space-x-10 space-x-[32px] justify-center">
              <Button className="w-full" color="reset" onClick={goBack}>
                戻る
              </Button>
              <Button
                className="w-full"
                color="primary"
                onClick={handleSubmitUpdate}
                id="button-change-password"
              >
                更新
              </Button>
            </div>
          </div>
        </div>
      </div>
    </BackendLayout>
  );
};

export default ChangePasswordPage;
